<template>
  <div class="map-container" :class="{ 'h-[80vh]': isMobile, 'h-full': !isMobile }">
    <!-- Desktop/Tablet Search Bar Overlay -->
    <div v-if="!isMobile" class="search-overlay">
      <div class="search-container">
        <div class="search-input-wrapper">
          <input
            v-model="searchQuery"
            @keyup.enter="searchPlace"
            @input="onSearchInput"
            @blur="hideSuggestions"
            type="text"
            placeholder="Search for a city or country..."
            class="search-input"
          />
          <button @click="searchPlace" class="search-button" :disabled="!searchQuery.trim()">
            <i class="fas fa-search"></i>
          </button>
        </div>
        
        <!-- Search Suggestions -->
        <div v-if="searchSuggestions.length > 0 && showSuggestions" class="suggestions-container">
          <div
            v-for="(suggestion, index) in searchSuggestions"
            :key="index"
            @click="selectSuggestion(suggestion)"
            class="suggestion-item"
          >
            <i class="fas fa-map-marker-alt suggestion-icon"></i>
            <div class="suggestion-content">
              <div class="suggestion-name">{{ suggestion.place_name }}</div>
              <div class="suggestion-context">{{ getContextText(suggestion) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Mobile Search Implementation -->
    <div v-if="isMobile" class="mobile-search-overlay">
      <!-- Collapsed Search Button -->
      <button 
        v-if="!mobileSearchExpanded" 
        @click="expandMobileSearch"
        class="mobile-search-button"
        aria-label="Open search"
      >
        <i class="fas fa-search"></i>
      </button>
      
      <!-- Expanded Search Bar -->
      <div v-if="mobileSearchExpanded" class="mobile-search-container">
        <div class="mobile-search-input-wrapper">
          <input
            ref="mobileSearchInput"
            v-model="searchQuery"
            @keyup.enter="searchPlace"
            @input="onSearchInput"
            @blur="hideMobileSuggestions"
            type="text"
            placeholder="Search for a city or country..."
            class="mobile-search-input"
          />
          <button @click="searchPlace" class="mobile-search-submit" :disabled="!searchQuery.trim()">
            <i class="fas fa-search"></i>
          </button>
          <button @click="collapseMobileSearch" class="mobile-search-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <!-- Mobile Search Suggestions -->
        <div v-if="searchSuggestions.length > 0 && showSuggestions" class="mobile-suggestions-container">
          <div
            v-for="(suggestion, index) in searchSuggestions"
            :key="index"
            @click="selectMobileSuggestion(suggestion)"
            class="mobile-suggestion-item"
          >
            <i class="fas fa-map-marker-alt mobile-suggestion-icon"></i>
            <div class="mobile-suggestion-content">
              <div class="mobile-suggestion-name">{{ suggestion.place_name }}</div>
              <div class="mobile-suggestion-context">{{ getContextText(suggestion) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Loading Indicator -->
    <div v-if="searchLoading" class="search-loading">
      <div class="loading-spinner"></div>
      <span>Searching...</span>
    </div>
    
    <div id="mapbox-map" class="map-container"></div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import threeboxManager from '@/utils/threebox.js'
import buildingModelsService from '@/services/buildingModelsService.js'

export default {
  name: 'MapboxMap',
  emits: {
    'quiz-complete': (payload) => payload && typeof payload === 'object',
    'wrong-guess': () => true,
    'next-clue': () => true,
    'loading': (loading) => typeof loading === 'boolean',
    'update-info': (data) => data && typeof data === 'object',
    'close-info': () => true
  },
  props: {
    activeTab: {
      type: String,
      required: true
    },
    quizCountryName: {
      type: String,
      default: ''
    },
    quizType: {
      type: String,
      default: null
    },
    mapSettings: {
      type: Object,
      default: () => ({
        provider: 'mapbox',
        style: 'light',
        countryOverlay: 'colored',
        showPlaceNames: true,
        showTectonicPlates: false,
        show3DTerrain: false,
        show3DBuildings: false
      })
    }
  },
  data() {
    return {
      map: null,
      markers: [],
      quizMarkers: [], // Separate array for quiz markers
      isMobile: window.innerWidth <= 768,
      searchQuery: '',
      searchSuggestions: [],
      showSuggestions: false,
      searchLoading: false,
      searchTimeout: null,
      animationTimeout: null, // Track animation timers
      hasSpunToRandomLocation: false, // Track if we've done the initial spin
      currentLocationIndex: -1, // Track current location in the array
      mobileSearchExpanded: false, // Track mobile search state
      buildingModelsLoaded: false, // Track if 3D models system is loaded
    };
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    this.initMap();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);

    // Clear any pending timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    // Clean up 3D models
    if (this.buildingModelsLoaded) {
      threeboxManager.dispose();
    }

    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    
    getInterestingLocations() {
      return [
        // Original locations
        { name: "Machu Picchu, Peru", coords: [-72.5450, -13.1631] },
        { name: "Santorini, Greece", coords: [25.4615, 36.3932] },
        { name: "Great Barrier Reef, Australia", coords: [145.7781, -16.2839] },
        { name: "Mount Fuji, Japan", coords: [138.7274, 35.3606] },
        { name: "Aurora Borealis, Iceland", coords: [-21.8174, 64.1466] },
        { name: "Serengeti, Tanzania", coords: [34.8333, -2.3333] },
        { name: "Banff National Park, Canada", coords: [-115.5708, 51.4968] },
        { name: "Maldives", coords: [73.2207, 3.2028] },
        { name: "Patagonia, Chile", coords: [-72.9301, -49.3103] },
        { name: "Bagan, Myanmar", coords: [94.8573, 21.1619] },
        { name: "Salar de Uyuni, Bolivia", coords: [-67.4891, -20.1338] },
        { name: "Norwegian Fjords, Norway", coords: [7.0784, 62.4720] },
        { name: "Petra, Jordan", coords: [35.4444, 30.3285] },
        { name: "Angkor Wat, Cambodia", coords: [103.8670, 13.4125] },
        { name: "Easter Island, Chile", coords: [-109.3497, -27.1127] },
        { name: "Madagascar", coords: [46.8691, -18.7669] },
        { name: "Scottish Highlands, Scotland", coords: [-4.4426, 57.2900] },
        { name: "Socotra Island, Yemen", coords: [53.9068, 12.4634] },
        { name: "Raja Ampat, Indonesia", coords: [130.8456, -0.2315] },
        { name: "Faroe Islands", coords: [-7.0926, 61.8926] },
        
        // New locations - Mexican cities
        { name: "Mexico City, Mexico", coords: [-99.1332, 19.4326] },
        { name: "Tijuana, Mexico", coords: [-117.0382, 32.5149] },
        { name: "Guadalajara, Mexico", coords: [-103.3496, 20.6597] },
        { name: "Oaxaca, Mexico", coords: [-96.7266, 17.0732] },
        { name: "Cancun, Mexico", coords: [-86.8515, 21.1619] },
        { name: "Holbox, Mexico", coords: [-87.3781, 21.5218] },
        
        // New locations - Major international cities
        { name: "New York, USA", coords: [-74.0060, 40.7128] },
        { name: "Dubai, UAE", coords: [55.2708, 25.2048] },
        { name: "Tokyo, Japan", coords: [139.6917, 35.6895] },
        { name: "Auckland, New Zealand", coords: [174.7633, -36.8485] },
        { name: "Shanghai, China", coords: [121.4737, 31.2304] },
        { name: "Sydney, Australia", coords: [151.2093, -33.8688] },
        { name: "Bali, Indonesia", coords: [115.0920, -8.3405] },
        { name: "Cairo, Egypt", coords: [31.2357, 30.0444] },
        { name: "Paris, France", coords: [2.3522, 48.8566] },
        { name: "London, UK", coords: [-0.1276, 51.5074] },
        { name: "Madrid, Spain", coords: [-3.7038, 40.4168] },
        { name: "Barcelona, Spain", coords: [2.1734, 41.3851] },
        { name: "Helsinki, Finland", coords: [24.9384, 60.1699] },
        { name: "Guangzhou, China", coords: [113.2644, 23.1291] },
        { name: "Oslo, Norway", coords: [10.7522, 59.9139] },
        { name: "Rio de Janeiro, Brazil", coords: [-43.1729, -22.9068] },
        { name: "Los Angeles, USA", coords: [-118.2437, 34.0522] },
        
        // New locations - Natural wonders
        { name: "Mount Everest, Nepal", coords: [86.9250, 27.9881] },
        { name: "Grand Canyon, USA", coords: [-112.1401, 36.1069] }
      ];
    },

    getRandomLocation() {
      const locations = this.getInterestingLocations();
      const randomIndex = Math.floor(Math.random() * locations.length);
      this.currentLocationIndex = randomIndex;
      return locations[randomIndex];
    },
    
    getMapStyle() {
      switch (this.mapSettings.style) {
        case 'satellite':
          // Use satellite-streets which includes place names
          return 'mapbox://styles/mapbox/satellite-streets-v12';
        case 'terrain':
          return 'mapbox://styles/mapbox/outdoors-v12';
        case 'light':
        default:
          return 'mapbox://styles/mapbox/light-v11';
      }
    },
    
    updateMapStyle() {
      if (this.map) {
        const newStyle = this.getMapStyle();
        this.map.setStyle(newStyle);
        
        // Re-add countries after style change
        this.map.once('style.load', () => {
          this.loadCountries();
          this.updatePlaceNamesVisibility();
          this.updateTectonicPlates();
          this.update3DTerrain();
          this.update3DBuildings();
        });
      }
    },
    
    update3DTerrain() {
      if (!this.map) return;
      
      if (this.mapSettings.show3DTerrain) {
        // Add terrain source if it doesn't exist
        if (!this.map.getSource('mapbox-dem')) {
          this.map.addSource('mapbox-dem', {
            'type': 'raster-dem',
            'url': 'mapbox://mapbox.mapbox-terrain-dem-v1',
            'tileSize': 512,
            'maxzoom': 14
          });
        }
        
        // Set terrain with kid-friendly exaggeration
        this.map.setTerrain({ 
          'source': 'mapbox-dem', 
          'exaggeration': 1.8  // Make mountains more dramatic for educational purposes
        });
        
        // Optional: Adjust camera for better 3D view
        if (this.map.getPitch() < 45) {
          this.map.easeTo({
            pitch: 60,
            duration: 1000
          });
        }
        
      } else {
        // Remove terrain
        this.map.setTerrain(null);
        
        // Reset camera pitch if it was high
        if (this.map.getPitch() > 45) {
          this.map.easeTo({
            pitch: 0,
            duration: 1000
          });
        }
      }
    },
    
    update3DBuildings() {
      if (!this.map) return;
      
      const layerId = '3d-buildings';
      
      if (this.mapSettings.show3DBuildings) {
        // Add 3D buildings layer if it doesn't exist
        if (!this.map.getLayer(layerId)) {
          this.map.addLayer({
            'id': layerId,
            'source': 'composite',
            'source-layer': 'building',
            'filter': ['==', 'extrude', 'true'],
            'type': 'fill-extrusion',
            'minzoom': 15,
            'paint': {
              'fill-extrusion-color': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                '#ff6b6b',  // Hover color - kid-friendly red
                '#4ecdc4'   // Default color - kid-friendly teal
              ],
              'fill-extrusion-height': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15, 0,
                15.05, ['get', 'height']
              ],
              'fill-extrusion-base': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15, 0,
                15.05, ['get', 'min_height']
              ],
              'fill-extrusion-opacity': 0.8
            }
          });
          
          // Add hover effect
          let hoveredBuildingId = null;
          
          this.map.on('mouseenter', layerId, (e) => {
            this.map.getCanvasContainer().style.cursor = 'pointer';
            if (e.features.length > 0) {
              if (hoveredBuildingId !== null) {
                this.map.setFeatureState(
                  { source: 'composite', sourceLayer: 'building', id: hoveredBuildingId },
                  { hover: false }
                );
              }
              hoveredBuildingId = e.features[0].id;
              this.map.setFeatureState(
                { source: 'composite', sourceLayer: 'building', id: hoveredBuildingId },
                { hover: true }
              );
            }
          });
          
          this.map.on('mouseleave', layerId, () => {
            this.map.getCanvasContainer().style.cursor = '';
            if (hoveredBuildingId !== null) {
              this.map.setFeatureState(
                { source: 'composite', sourceLayer: 'building', id: hoveredBuildingId },
                { hover: false }
              );
            }
            hoveredBuildingId = null;
          });
        }
      } else {
        // Remove 3D buildings layer
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      }
    },
    
    updateTectonicPlates() {
      if (!this.map) return;
      
      const layerId = 'tectonic-plates';
      
      if (this.mapSettings.showTectonicPlates) {
        // Add tectonic plates layer if it doesn't exist
        if (!this.map.getLayer(layerId)) {
          // First, add the source for tectonic plates data
          if (!this.map.getSource('tectonic-plates')) {
            this.map.addSource('tectonic-plates', {
              type: 'geojson',
              data: 'https://raw.githubusercontent.com/fraxen/tectonicplates/master/GeoJSON/PB2002_boundaries.json'
            });
          }
          
          // Add the layer for tectonic plate boundaries
          this.map.addLayer({
            'id': layerId,
            'type': 'line',
            'source': 'tectonic-plates',
            'paint': {
              'line-color': '#ff6b35',  // Orange-red color for tectonic plates
              'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                4, 1,    // At zoom 4, line width is 1
                8, 2,    // At zoom 8, line width is 2
                12, 3    // At zoom 12, line width is 3
              ],
              'line-opacity': 0.8
            }
          });
        }
      } else {
        // Remove tectonic plates layer
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
        
        // Remove source if no longer needed
        if (this.map.getSource('tectonic-plates')) {
          this.map.removeSource('tectonic-plates');
        }
      }
    },
    
    updatePlaceNamesVisibility() {
      if (!this.map) return;
      
      const visibility = this.mapSettings.showPlaceNames ? 'visible' : 'none';
      
      // Get all layers from current style
      const style = this.map.getStyle();
      if (!style || !style.layers) return;
      
      // Find all symbol layers that contain text/labels
      const textLayers = style.layers.filter(layer => {
        if (layer.type !== 'symbol') return false;
        
        const layerId = layer.id.toLowerCase();
        const sourceLayer = layer['source-layer'] ? layer['source-layer'].toLowerCase() : '';
        
        // Keywords that indicate place names or labels
        const placeKeywords = [
          'place', 'label', 'city', 'town', 'village', 'settlement',
          'country', 'state', 'region', 'poi', 'text', 'name',
          'admin', 'capital', 'major', 'minor'
        ];
        
        return placeKeywords.some(keyword => 
          layerId.includes(keyword) || sourceLayer.includes(keyword)
        );
      });
      
      // Apply visibility to found text layers
      textLayers.forEach(layer => {
        try {
          this.map.setLayoutProperty(layer.id, 'visibility', visibility);
        } catch (e) {
          console.warn(`Could not set visibility for layer: ${layer.id}`, e);
        }
      });
      
      // Also try known layer names as fallback
      const knownLayers = [
        'country-label', 'state-label', 'place-city-lg', 'place-city-md', 'place-city-sm',
        'place-town', 'place-village', 'settlement-label', 'poi-label',
        'place-country-major', 'place-country-minor', 'place-state', 'place-province',
        'admin-0-label', 'admin-1-label', 'place-capital', 'place-city-capital'
      ];
      
      knownLayers.forEach(layerId => {
        try {
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(layerId, 'visibility', visibility);
          }
        } catch (e) {
          // Layer doesn't exist, ignore
        }
      });
    },
    
    removeCountryLayers() {
      if (!this.map) return;
      
      // Remove existing country layers
      const layersToRemove = ['country-fills', 'country-borders'];
      layersToRemove.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      
      // Remove source if it exists
      if (this.map.getSource('countries')) {
        this.map.removeSource('countries');
      }
    },
    
    initMap() {
      // Set Mapbox access token from environment variable
      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_ACCESS_TOKEN;

      // Get the map style based on settings
      const mapStyle = this.getMapStyle();

      // Initialize the map with a starting view that will spin to random location
      this.map = new mapboxgl.Map({
        container: 'mapbox-map',
        style: mapStyle,
        center: [0, 0], // Start at center of world
        zoom: 1, // Start zoomed out to show globe
        projection: 'globe'
      });

      // Set up the globe atmosphere when style loads
      this.map.on('style.load', () => {
        this.map.setFog({
          color: "#000000",
          "horizon-blend": 0.2,
          "high-color": "#1a1a1a",
          "space-color": "#000000",
          "star-intensity": 0.5
        });
      });

      // Add navigation controls (only on desktop/tablet)
      if (!this.isMobile) {
        this.map.addControl(new mapboxgl.NavigationControl());
      }

      // Load countries and set up click handling
      this.map.on('load', () => {
        this.loadCountries();
        this.setupClickHandler();
        this.updatePlaceNamesVisibility();
        this.updateTectonicPlates();
        this.update3DTerrain();
        this.update3DBuildings();
        this.toggle3DBuildingModels();
        
        // Perform the initial random location spin only if no slug in URL
        if (!this.$route.params.slug) {
          this.spinToRandomLocation();
        }
      });
    },

    async loadCountries() {
      // If overlay is disabled, remove existing layers and return
      if (this.mapSettings.countryOverlay === 'none') {
        this.removeCountryLayers();
        return;
      }
      
      try {
        const response = await fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson');
        const geojson = await response.json();

        // Kid-friendly colors for countries
        const kidFriendlyColors = [
          '#FF5733', '#FFC300', '#DAF7A6', '#FF33FF', '#33FF57', '#33C1FF',
          '#FF6F61', '#FFD700', '#FFB6C1', '#87CEEB', '#9370DB', '#FF4500',
          '#32CD32', '#4682B4', '#8A2BE2', '#FF69B4', '#FF8C00', '#7FFF00',
          '#00CED1', '#ADFF2F', '#20B2AA', '#F08080', '#FFA07A', '#6495ED',
          '#EE82EE', '#FFDAB9', '#B0E0E6', '#FF7F50', '#FF1493'
        ];

        const colorMapping = {};
        let colorIndex = 0;

        // Debug: Check the structure of the first feature
        if (geojson.features.length > 0) {
          console.log('First feature properties:', Object.keys(geojson.features[0].properties));
          console.log('Sample feature:', geojson.features[0].properties);
        }

        // Assign colors to countries using multiple fallback methods
        geojson.features.forEach((feature, index) => {
          // Try different possible ISO code properties
          const isoCode = feature.properties['ISO_A3'] || 
                          feature.properties['iso_a3'] || 
                          feature.properties['ISO3'] || 
                          feature.properties['iso3'] ||
                          feature.properties['ADM0_A3'] ||
                          feature.properties['NAME'] || // Fallback to country name
                          `country_${index}`; // Last resort: use index
          
          if (isoCode && isoCode !== '-99' && isoCode !== 'null') {
            const assignedColor = kidFriendlyColors[colorIndex % kidFriendlyColors.length];
            colorMapping[isoCode] = assignedColor;
            feature.properties.color = assignedColor;
            feature.properties.colorKey = isoCode; // Store which key we're using for color
            colorIndex++;
            
            if (index < 10) {
              console.log(`Country ${index}: ${feature.properties['NAME']} -> ${isoCode} -> ${assignedColor}`);
            }
          } else if (index < 5) {
            console.log('Country without valid ISO code:', feature.properties);
          }
        });

        // Add countries source (check if it doesn't already exist)
        if (!this.map.getSource('countries')) {
          this.map.addSource('countries', {
            type: 'geojson',
            data: geojson
          });
        } else {
          // Update existing source data
          this.map.getSource('countries').setData(geojson);
        }

        // Build match expression for country colors - use the colorKey we stored
        const isoProperty = 'colorKey'; // We'll use the colorKey we stored in each feature
        
        console.log('Using ISO property:', isoProperty);
        const matchExpression = ['match', ['get', isoProperty]];
        
        // Debug: Check if we have any countries
        console.log('Number of countries found:', Object.keys(colorMapping).length);
        console.log('Sample countries:', Object.keys(colorMapping).slice(0, 5));
        
        // Add country-color pairs to match expression
        Object.keys(colorMapping).forEach((isoCode) => {
          matchExpression.push(isoCode, colorMapping[isoCode]);
        });
        
        // Ensure we have at least one country, otherwise use a simple fallback
        if (Object.keys(colorMapping).length === 0) {
          console.warn('No countries found with ISO_A3 codes, using simple color');
          // Use a simple color instead of match expression
          let fallbackColor, fallbackOpacity;
          switch (this.mapSettings.countryOverlay) {
            case 'colored':
              fallbackColor = '#FF5733';
              fallbackOpacity = 0.3;
              break;
            case 'white':
              fallbackColor = '#ffffff';
              fallbackOpacity = 0.1;
              break;
            case 'none':
            default:
              return; // Don't add any layers
          }
          
          if (!this.map.getLayer('country-fills')) {
            this.map.addLayer({
              id: 'country-fills',
              type: 'fill',
              source: 'countries',
              paint: {
                'fill-color': fallbackColor,
                'fill-opacity': fallbackOpacity
              }
            });
          } else {
            this.map.setPaintProperty('country-fills', 'fill-color', fallbackColor);
            this.map.setPaintProperty('country-fills', 'fill-opacity', fallbackOpacity);
          }
          return;
        }
        
        matchExpression.push('#CCCCCC'); // Fallback color for unmatched countries
        
        console.log('Match expression length:', matchExpression.length);

        // Add country fill layer with conditional coloring based on overlay setting
        let fillColor, fillOpacity;
        switch (this.mapSettings.countryOverlay) {
          case 'colored':
            fillColor = matchExpression;
            fillOpacity = 0.3;
            break;
          case 'white':
            fillColor = '#ffffff';
            fillOpacity = 0; // Completely transparent - only borders visible
            break;
          case 'none':
          default:
            // This shouldn't happen as we return early, but just in case
            return;
        }
        
        if (!this.map.getLayer('country-fills')) {
          this.map.addLayer({
            id: 'country-fills',
            type: 'fill',
            source: 'countries',
            paint: {
              'fill-color': fillColor,
              'fill-opacity': fillOpacity
            }
          });
        } else {
          // Update existing layer paint properties
          this.map.setPaintProperty('country-fills', 'fill-color', fillColor);
          this.map.setPaintProperty('country-fills', 'fill-opacity', fillOpacity);
        }

        // Add country borders layer
        if (!this.map.getLayer('country-borders')) {
          this.map.addLayer({
            id: 'country-borders',
            type: 'line',
            source: 'countries',
            paint: {
              'line-color': '#ffffff',
              'line-width': 1
            }
          });
        }

        // Set water color if the layer exists
        if (this.map.getLayer('water')) {
          this.map.setPaintProperty('water', 'fill-color', '#d6f1ff');
        }

      } catch (error) {
        console.error('Error loading countries:', error);
      }
    },

    setupClickHandler() {
      this.map.on('click', async (e) => {
        try {
          const { lng, lat } = e.lngLat;
          
          // Validate coordinates
          if (!lng || !lat || isNaN(lng) || isNaN(lat)) {
            console.error('Invalid coordinates from map click:', { lng, lat });
            return;
          }
          
          if (this.activeTab !== 'quiz') {
            // Close any existing info modal/drawer to reset state
            this.$emit('close-info');
            
            // Regular info mode - replace previous marker
            this.addMarker([lng, lat]);
            
            // Get location details first
            const locationDetails = await this.reverseGeocode(lng, lat);
            
            // Fetch place information for info tab
            if (locationDetails && locationDetails.displayName) {
              await this.fetchPlacesInfo(locationDetails.displayName, locationDetails);
            } else if (locationDetails && locationDetails.country) {
              // Fallback to basic place name construction
              const placeName = `${locationDetails.city || ''}, ${locationDetails.state || ''}, ${locationDetails.country}`.replace(/^,\s*/, '').replace(/,\s*,/, ',');
              await this.fetchPlacesInfo(placeName, locationDetails);
            } else {
              await this.fetchCoordinatesInfo(lat, lng, locationDetails);
            }
          } else {
            // Quiz mode - add colored markers and keep them
            const locationDetails = await this.reverseGeocode(lng, lat);

            // Debug logging for country matching
            console.log('Quiz country matching:', {
              expected: this.quizCountryName,
              received: locationDetails?.country,
              match: locationDetails?.country === this.quizCountryName
            });

            const isCorrect = locationDetails && locationDetails.country === this.quizCountryName;
            this.addQuizMarker([lng, lat], isCorrect);

            if (isCorrect) {
              this.$emit('quiz-complete', { lat, lng });
            } else {
              // Different behavior for flag quiz vs clues quiz
              console.log('Wrong answer - quiz type:', this.quizType);
              if (this.quizType === 'flags') {
                // Flag quiz: emit wrong-guess to increment attempts
                console.log('Emitting wrong-guess for flag quiz');
                this.$emit('wrong-guess');
              } else {
                // Clues quiz: show next clue
                console.log('Emitting next-clue for clues quiz');
                this.$emit('next-clue');
              }
            }
          }
        } catch (error) {
          console.error('Error handling map click:', error);
        }
      });
    },

    async reverseGeocode(lng, lat) {
      try {
        const accessToken = mapboxgl.accessToken;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}`;
        
        const response = await fetch(url);
        const data = await response.json();

        if (data.features.length > 0) {
          const place = data.features[0];
          
          // Extract location components from the main feature and context
          let city = null;
          let state = null;
          let country = null;
          let locality = null;
          
          // First, check the main place name (could be a city, locality, or POI)
          if (place.text) {
            // Determine what type of place this is based on place_type
            const placeTypes = place.place_type || [];
            if (placeTypes.includes('place') || placeTypes.includes('locality')) {
              city = place.text;
            } else if (placeTypes.includes('poi') || placeTypes.includes('address')) {
              locality = place.text; // Use as locality if it's a POI or address
            }
          }
          
          // Parse the context array for administrative divisions
          if (place.context) {
            place.context.forEach(ctx => {
              const id = ctx.id || '';
              const text = ctx.text;
              
              if (id.includes('place') && !city) {
                city = text;
              } else if (id.includes('locality') && !locality) {
                locality = text;
              } else if (id.includes('neighborhood') && !locality && !city) {
                locality = text;
              } else if (id.includes('district') && !city && !locality) {
                locality = text;
              } else if (id.includes('region') || id.includes('province') || id.includes('state')) {
                state = text;
              } else if (id.includes('country')) {
                country = text;
              }
            });
          }
          
          // Build a clean place name using the best available data
          const displayName = this.buildPlaceName({ city, locality, state, country, originalText: place.text });
          
          return { 
            city: city || locality, 
            state, 
            country, 
            displayName,
            coordinates: { lng, lat }
          };
        } else {
          return { city: null, state: null, country: null, displayName: null, coordinates: { lng, lat } };
        }
      } catch (error) {
        console.error("Error with reverse geocoding:", error);
        return { city: null, state: null, country: null, displayName: null, coordinates: { lng, lat } };
      }
    },
    
    buildPlaceName({ city, locality, state, country, originalText }) {
      const parts = [];
      
      // Use city if available, otherwise use locality
      const mainPlace = city || locality || originalText;
      if (mainPlace) {
        parts.push(mainPlace);
      }
      
      // Add state if available and different from main place
      if (state && state !== mainPlace) {
        parts.push(state);
      }
      
      // Always add country if available
      if (country) {
        parts.push(country);
      }
      
      // If we have no meaningful place info, return null
      if (parts.length === 0 || (parts.length === 1 && !country)) {
        return null;
      }
      
      return parts.join(', ');
    },
    
    async spinToRandomLocation() {
      if (this.hasSpunToRandomLocation) {
        return; // Only do this once on initial load
      }
      
      this.hasSpunToRandomLocation = true;
      const randomLocation = this.getRandomLocation();
      const [lng, lat] = randomLocation.coords;
      
      console.log(`🌍 Spinning to random location: ${randomLocation.name}`);
      
      // Create an exciting spinning animation to the random location
      await this.map.easeTo({
        center: [lng, lat],
        zoom: 11, // Zoom in closer to show more details
        pitch: 45, // Add 3D perspective to show terrain better
        duration: 4000, // 4 second animation
        essential: true,
        easing: (t) => {
          // Custom easing function for a more dramatic effect
          return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        }
      });
      
      // Wait a moment for the animation to complete, then fetch location info
      this.animationTimeout = setTimeout(async () => {
        // Add a marker to show where we landed
        this.addMarker([lng, lat]);

        // Get detailed location information
        const locationDetails = await this.reverseGeocode(lng, lat);

        // Fetch place information to show in sidebar
        if (locationDetails.displayName) {
          await this.fetchPlacesInfo(locationDetails.displayName, locationDetails);
        } else if (locationDetails.country) {
          const placeName = `${locationDetails.city || ''}, ${locationDetails.state || ''}, ${locationDetails.country}`.replace(/^,\s*/, '').replace(/,\s*,/, ',');
          await this.fetchPlacesInfo(placeName, locationDetails);
        } else {
          // Fallback to the known name if reverse geocoding fails
          await this.fetchPlacesInfo(randomLocation.name, { displayName: randomLocation.name });
        }
      }, 4200); // Slight delay after animation completes
    },

    addMarker(coordinates) {
      // Guard: check if map exists before adding marker
      if (!this.map || !this.map.getContainer()) {
        console.warn('Cannot add marker: map not available');
        return;
      }

      // Remove existing regular markers (not quiz markers)
      this.deleteMarkers();

      // Create new marker
      const marker = new mapboxgl.Marker({
        color: '#FF0000'
      })
        .setLngLat(coordinates)
        .addTo(this.map);

      this.markers.push(marker);
    },

    async goToNextAmazingPlace() {
      const locations = this.getInterestingLocations();
      
      // Get the next location index (cycle through the array)
      const nextIndex = (this.currentLocationIndex + 1) % locations.length;
      this.currentLocationIndex = nextIndex;
      
      const nextLocation = locations[nextIndex];
      const [lng, lat] = nextLocation.coords;
      
      console.log(`🌍 Going to next amazing place: ${nextLocation.name}`);
      
      // Phase 1: Zoom out to world view (1.5 seconds)
      await this.map.easeTo({
        center: [0, 20], // Center on world with slight offset to show more land
        zoom: 1.5, // World view
        duration: 1500,
        essential: true,
        easing: (t) => {
          // Smooth ease-out for zooming out
          return 1 - Math.pow(1 - t, 3);
        }
      });
      
      // Brief pause to let user see the world view
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Phase 2: Zoom into the destination (2.5 seconds)
      await this.map.easeTo({
        center: [lng, lat],
        zoom: 11, // Zoom in closer to show more details
        pitch: 45, // Add 3D perspective to show terrain better
        duration: 2500,
        essential: true,
        easing: (t) => {
          // Smooth ease-in-out for dramatic zoom-in effect
          return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
        }
      });
      
      // Wait a moment for the animation to complete, then fetch location info
      this.animationTimeout = setTimeout(async () => {
        // Add a marker to show where we landed
        this.addMarker([lng, lat]);

        // Get detailed location information
        const locationDetails = await this.reverseGeocode(lng, lat);

        // Fetch place information to show in sidebar
        if (locationDetails.displayName) {
          await this.fetchPlacesInfo(locationDetails.displayName, locationDetails);
        } else if (locationDetails.country) {
          const placeName = `${locationDetails.city || ''}, ${locationDetails.state || ''}, ${locationDetails.country}`.replace(/^,\s*/, '').replace(/,\s*,/, ',');
          await this.fetchPlacesInfo(placeName, locationDetails);
        } else {
          // Fallback to the known name if reverse geocoding fails
          await this.fetchPlacesInfo(nextLocation.name, { displayName: nextLocation.name });
        }
      }, 500); // Shorter delay since we already have the pause built in
    },

    addQuizMarker(coordinates, isCorrect) {
      // Guard: check if map exists before adding marker
      if (!this.map || !this.map.getContainer()) {
        console.warn('Cannot add quiz marker: map not available');
        return;
      }

      // Create colored marker based on correctness
      const marker = new mapboxgl.Marker({
        color: isCorrect ? '#00FF00' : '#FF0000' // Green for correct, red for wrong
      })
        .setLngLat(coordinates)
        .addTo(this.map);

      this.quizMarkers.push(marker);
    },

    deleteMarkers() {
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
    },

    deleteQuizMarkers() {
      this.quizMarkers.forEach(marker => marker.remove());
      this.quizMarkers = [];
    },

    deleteAllMarkers() {
      this.deleteMarkers();
      this.deleteQuizMarkers();
    },

    async fetchPlacesInfo(placeName, locationDetails = null) {
      if (!placeName) {
        console.error('fetchPlacesInfo called with empty placeName');
        return;
      }
      
      // Emit loading with place-specific context for custom loading messages
      this.$emit('loading', true, { 
        placeName: locationDetails?.displayName || placeName,
        type: 'place'
      });
      
      try {
        // Get auth token for request
        const token = this.$store.getters['auth/isAuthenticated'] ? this.$store.state.auth.token : null;
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Build URL with coordinates if available
        let url = `${process.env.VUE_APP_API_URL}/api/placeInfo?placeName=${encodeURIComponent(placeName)}`;
        
        
        const response = await fetch(url, {
          headers
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data) {
          this.$emit('update-info', data);
        }
      } catch (error) {
        console.error('Error fetching place info:', error);
      } finally {
        this.$emit('loading', false);
      }
    },

    async fetchCoordinatesInfo(lat, lng, locationDetails = null) {
      if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
        console.error('fetchCoordinatesInfo called with invalid coordinates:', { lat, lng });
        return;
      }
      
      // Emit loading with coordinate context
      this.$emit('loading', true, { 
        placeName: locationDetails?.displayName || `Location ${lat.toFixed(2)}, ${lng.toFixed(2)}`,
        type: 'coordinates'
      });
      
      try {
        // Get auth token for request
        const token = this.$store.getters['auth/isAuthenticated'] ? this.$store.state.auth.token : null;
        const headers = {
          'Content-Type': 'application/json'
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${process.env.VUE_APP_API_URL}/api/coordinates?lat=${lat}&lng=${lng}`, {
          headers
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        if (data) {
          this.$emit('update-info', data);
        }
      } catch (error) {
        console.error('Error fetching coordinates info:', error);
      } finally {
        this.$emit('loading', false);
      }
    },

    // Search functionality
    onSearchInput() {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      // Hide suggestions if query is empty
      if (!this.searchQuery.trim()) {
        this.showSuggestions = false;
        this.searchSuggestions = [];
        return;
      }
      
      // Debounce search suggestions
      this.searchTimeout = setTimeout(() => {
        this.fetchSearchSuggestions();
      }, 300);
    },

    async fetchSearchSuggestions() {
      if (!this.searchQuery.trim()) return;
      
      try {
        const accessToken = mapboxgl.accessToken;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(this.searchQuery)}.json?access_token=${accessToken}&limit=5&types=place,country,region`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        this.searchSuggestions = data.features || [];
        this.showSuggestions = this.searchSuggestions.length > 0;
      } catch (error) {
        console.error('Error fetching search suggestions:', error);
        this.searchSuggestions = [];
        this.showSuggestions = false;
      }
    },

    async searchPlace() {
      if (!this.searchQuery.trim()) return;
      
      this.searchLoading = true;
      this.showSuggestions = false;
      
      try {
        const accessToken = mapboxgl.accessToken;
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(this.searchQuery)}.json?access_token=${accessToken}&limit=1`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          const place = data.features[0];
          await this.flyToPlace(place);
        } else {
          alert('Place not found. Please try a different search term.');
        }
      } catch (error) {
        console.error('Error searching for place:', error);
        alert('Error searching for place. Please try again.');
      } finally {
        this.searchLoading = false;
      }
    },

    // Method to be called externally (from parent component)
    performSearch(query) {
      this.searchQuery = query;
      this.searchPlace();
    },

    selectSuggestion(suggestion) {
      this.searchQuery = suggestion.place_name;
      this.showSuggestions = false;
      this.flyToPlace(suggestion);
    },

    async flyToPlace(place) {
      const [lng, lat] = place.center;
      
      // Fly to the location with smooth animation
      this.map.flyTo({
        center: [lng, lat],
        zoom: 10,
        duration: 2000 // 2 second animation
      });
      
      // Add marker at the location
      if (this.activeTab !== 'quiz') {
        this.addMarker([lng, lat]);
        
        // Fetch and display place information
        await this.fetchPlacesInfo(place.place_name);
      }
      
      // Clear search
      this.searchQuery = '';
    },

    // Hide suggestions when clicking outside
    hideSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200); // Small delay to allow clicking on suggestions
    },
    
    // Mobile search methods
    expandMobileSearch() {
      this.mobileSearchExpanded = true;
      // Focus the input after the next DOM update
      this.$nextTick(() => {
        if (this.$refs.mobileSearchInput) {
          this.$refs.mobileSearchInput.focus();
        }
      });
    },
    
    collapseMobileSearch() {
      this.mobileSearchExpanded = false;
      this.showSuggestions = false;
      // Clear search query when collapsing (optional)
      // this.searchQuery = '';
    },
    
    // Hide mobile suggestions with delay to allow clicking
    hideMobileSuggestions() {
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200); // Small delay to allow clicking on suggestions
    },
    
    selectMobileSuggestion(suggestion) {
      this.selectSuggestion(suggestion);
      this.collapseMobileSearch();
    },
    
    // Extract context text from suggestion object
    getContextText(suggestion) {
      if (!suggestion) return '';
      
      // First try to get context (array of objects with text property)
      if (suggestion.context && Array.isArray(suggestion.context)) {
        const contextTexts = suggestion.context
          .map(ctx => ctx.text || ctx)
          .filter(text => typeof text === 'string');
        if (contextTexts.length > 0) {
          return contextTexts.join(', ');
        }
      }
      
      // Fallback to place_type if available
      if (suggestion.place_type && Array.isArray(suggestion.place_type)) {
        return suggestion.place_type.join(', ');
      }
      
      // Final fallback to properties if available
      if (suggestion.properties && suggestion.properties.category) {
        return suggestion.properties.category;
      }
      
      return '';
    },
    
    flyToCoordinates(lat, lng, placeName = null, skipFetch = false) {
      if (!this.map || !lat || !lng || isNaN(lat) || isNaN(lng)) {
        console.error('Invalid coordinates for flyToCoordinates:', { lat, lng });
        return;
      }
      
      // Clear existing markers first
      this.deleteMarkers();
      
      // Fly to the coordinates with animation
      this.map.flyTo({
        center: [lng, lat],
        zoom: 12,
        duration: 2000,
        essential: true
      });
      
      // Add a marker at the location
      setTimeout(() => {
        this.addMarker([lng, lat]);
        
        // Skip API call if we already have the data (e.g., from slug route)
        if (!skipFetch) {
          // Fetch place information if we have a place name
          if (placeName) {
            this.fetchPlacesInfo(placeName, { displayName: placeName });
          } else {
            // Otherwise fetch by coordinates
            this.fetchCoordinatesInfo(lat, lng);
          }
        }
      }, 500);
    },

    // 3D Building Models Methods
    init3DBuildingModels() {
      if (!this.mapSettings.show3DModels || this.buildingModelsLoaded) return;
      
      try {
        console.log('Initializing 3D building models...');
        
        // Initialize threebox with this map
        threeboxManager.initialize(this.map);
        
        // Add building models layer
        threeboxManager.addBuildingModelsLayer();
        
        // Set up event listeners for building interactions
        this.setupBuildingEventListeners();
        
        this.buildingModelsLoaded = true;
        console.log('3D building models initialized successfully');
      } catch (error) {
        console.error('Failed to initialize 3D building models:', error);
      }
    },

    setupBuildingEventListeners() {
      // Listen for building click events
      window.addEventListener('buildingClicked', (event) => {
        const { building, coordinates } = event.detail;
        console.log(`Building clicked: ${building.name}`);
        
        // Fly to building location
        this.map.flyTo({
          center: coordinates,
          zoom: 16,
          duration: 2000
        });
        
        // Emit event to parent component to show building info
        this.$emit('update-info', {
          name: building.name,
          country: building.country,
          city: building.city,
          description: `Famous landmark in ${building.city}, ${building.country}`,
          coordinates: `${coordinates[1]},${coordinates[0]}`,
          lat: coordinates[1],
          lng: coordinates[0],
          slug: building.id,
          country_code: building.country.toLowerCase().slice(0, 2),
          landmarks: [
            {
              name: building.name,
              description: `Iconic ${building.name} located in ${building.city}`,
              slug: building.id
            }
          ],
          famous_places: [],
          traditional_foods: [],
          native_species: [],
          famous_people: [],
          important_dates: [],
          languages_spoken: [],
          history: `${building.name} is one of the most famous landmarks in ${building.city}, ${building.country}.`,
          territorial_divisions: {
            region: building.city
          },
          images: []
        });
      });
    },

    highlightBuilding(buildingId) {
      if (this.buildingModelsLoaded) {
        threeboxManager.highlightBuilding(buildingId);
      }
    },

    toggle3DBuildingModels() {
      if (this.mapSettings.show3DModels && this.map && !this.buildingModelsLoaded) {
        this.init3DBuildingModels();
      } else if (!this.mapSettings.show3DModels && this.buildingModelsLoaded) {
        // Remove 3D models layer
        if (this.map.getLayer('3d-buildings-models')) {
          this.map.removeLayer('3d-buildings-models');
        }
        threeboxManager.dispose();
        this.buildingModelsLoaded = false;
      }
    },

    getBuildingAt(lng, lat) {
      return buildingModelsService.getBuildingAt(lng, lat);
    }
  },
  watch: {
    activeTab(newTab, oldTab) {
      if (oldTab === 'quiz' && newTab !== 'quiz') {
        // Leaving quiz mode - remove all quiz markers
        this.deleteQuizMarkers();
      } else if (newTab !== 'quiz') {
        // Regular tab switch - only remove regular markers
        this.deleteMarkers();
      }
      // When entering quiz mode, keep existing markers until quiz starts
    },
    
    quizCountryName(newCountry, oldCountry) {
      // New quiz starting - clear previous quiz markers
      if (newCountry && newCountry !== oldCountry) {
        this.deleteQuizMarkers();
      }
    },
    
    'mapSettings.style'(newStyle, oldStyle) {
      if (newStyle !== oldStyle) {
        this.updateMapStyle();
      }
    },
    
    'mapSettings.countryOverlay'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        // Reload countries with new overlay setting
        this.loadCountries();
      }
    },
    
    'mapSettings.showPlaceNames'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        this.updatePlaceNamesVisibility();
      }
    },
    
    'mapSettings.show3DTerrain'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        this.update3DTerrain();
      }
    },
    
    'mapSettings.show3DBuildings'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        this.update3DBuildings();
      }
    },
    
    'mapSettings.showTectonicPlates'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        this.updateTectonicPlates();
      }
    },

    'mapSettings.show3DModels'(newValue, oldValue) {
      if (newValue !== oldValue && this.map) {
        this.toggle3DBuildingModels();
      }
    }
  }
};
</script>

<style scoped>
#mapbox-map {
  height: 100%;
  width: 100%;
}

.map-container {
  position: relative;
  width: 100%;
}

/* Search Overlay Styles */
.search-overlay {
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  z-index: 10;
  display: flex;
  justify-content: center;
}

.search-container {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.search-input-wrapper {
  display: flex;
  background: white;
  border-radius: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.search-input {
  flex: 1;
  padding: 12px 20px;
  border: none;
  outline: none;
  font-size: 16px;
  border-radius: 25px 0 0 25px;
}

.search-input::placeholder {
  color: #999;
}

.search-button {
  background: #3498db;
  color: white;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  border-radius: 0 25px 25px 0;
  transition: background-color 0.3s;
  min-width: 50px;
}

.search-button:hover:not(:disabled) {
  background: #2980b9;
}

.search-button:disabled {
  background: #bdc3c7;
  cursor: not-allowed;
}

.search-button i {
  font-size: 16px;
}

/* Search Suggestions Styles */
.suggestions-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 0 0 15px 15px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 11;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-icon {
  color: #3498db;
  margin-right: 12px;
  min-width: 16px;
}

.suggestion-content {
  flex: 1;
}

.suggestion-name {
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.suggestion-context {
  font-size: 12px;
  color: #666;
}

/* Search Loading Indicator */
.search-loading {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  padding: 10px 20px;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 12;
  font-size: 14px;
  color: #333;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .search-overlay {
    left: 10px;
    right: 10px;
    top: 10px; /* Bring closer to header */
  }
  
  .search-container {
    max-width: none;
  }
  
  .search-input {
    font-size: 14px;
    padding: 10px 16px;
  }
  
  .search-button {
    padding: 10px 14px;
  }
  
  .suggestion-item {
    padding: 10px 12px;
  }
}

/* Mobile Search Styles */
.mobile-search-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  z-index: 10;
}

/* Collapsed Search Button */
.mobile-search-button {
  position: absolute;
  top: 0;
  left: 0;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 22px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.mobile-search-button:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 18px rgba(102, 126, 234, 0.4);
}

.mobile-search-button i {
  font-size: 18px;
}

/* Expanded Search Container */
.mobile-search-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  animation: expandIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes expandIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.mobile-search-input-wrapper {
  display: flex;
  align-items: center;
  padding: 8px;
  gap: 8px;
}

.mobile-search-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  color: #1a202c;
  outline: none;
  transition: all 0.3s ease;
}

.mobile-search-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.mobile-search-input::placeholder {
  color: #a0aec0;
}

.mobile-search-submit {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.mobile-search-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-1px);
}

.mobile-search-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-search-close {
  width: 44px;
  height: 44px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.mobile-search-close:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.3);
}

/* Mobile Search Suggestions */
.mobile-suggestions-container {
  max-height: 300px;
  overflow-y: auto;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
}

.mobile-suggestion-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f7fafc;
}

.mobile-suggestion-item:hover {
  background: #f8fafc;
}

.mobile-suggestion-item:last-child {
  border-bottom: none;
}

.mobile-suggestion-icon {
  color: #667eea;
  margin-right: 12px;
  font-size: 14px;
  flex-shrink: 0;
}

.mobile-suggestion-content {
  flex: 1;
  min-width: 0;
}

.mobile-suggestion-name {
  font-weight: 500;
  color: #1a202c;
  font-size: 14px;
  margin-bottom: 2px;
}

.mobile-suggestion-context {
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>